import { Editor, EditorContent } from '@tiptap/react';
import clsx from 'clsx';
import { ActionData, Toolbar, ToolbarProvider } from 'copilot-design-system';
import { useState } from 'react';

import { AddLinkModal } from './AddLinkModal';
import { EDITOR_ACTIONS_CONFIG } from './constants';
import { HighlightToolbar } from './highlight-menu/HighlightToolbar';
import { useEditorActions } from './hooks/useEditorActions';
import { RichTextStyles } from './RichTextStyles';

/**
 * RichTextInput component is a wrapper around the tiptap editor that provides a toolbar and highlight menu.
 * @param editor - The tiptap editor instance
 * @param classes - The classes to apply to the container and toolbar
 * @param supportActions - The actions to support in the toolbar and highlight menu
 * @param showTopToolbar - Whether to show the top toolbar
 * @returns
 */
export const RichTextInput = ({
  editor,
  classes,
  supportActions,
  showTopToolbar = true,
  showHighlightToolbar = true,
}: {
  editor: Editor;
  classes?: {
    container?: string;
    toolbar?: string;
  };
  supportActions: { toolbar?: string[]; highlight?: string[] };
  showTopToolbar?: boolean;
  showHighlightToolbar?: boolean;
}) => {
  const { getActionState, onAction } = useEditorActions(editor);

  const [showLinkModal, setShowLinkModal] = useState(false);

  /**
   * Handle the action formatting or if the action is
   * a non formatting action such as link, it handles
   * showing the link modal.
   * @param actionData - The action data to handle
   */
  const handleAction = (actionData: ActionData) => {
    if (actionData.action === 'link') {
      setShowLinkModal(true);
    } else {
      onAction(actionData);
    }
  };

  return (
    <ToolbarProvider
      config={EDITOR_ACTIONS_CONFIG}
      onAction={handleAction}
      getActionState={getActionState}
    >
      {showHighlightToolbar && (
        <HighlightToolbar
          tiptapEditor={editor}
          overrideActions={supportActions.highlight}
        />
      )}
      {showTopToolbar && (
        <Toolbar
          variant="sticky"
          overrideActions={supportActions.toolbar}
          className={clsx(
            classes?.toolbar,
            'w-[-webkit-fill-available] sticky top-0 right-0 z-10',
          )}
        />
      )}
      <RichTextStyles>
        <div className={classes?.container}>
          <EditorContent editor={editor} />
        </div>
      </RichTextStyles>
      <AddLinkModal
        editor={editor}
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
      />
    </ToolbarProvider>
  );
};
